/**
 * @file     BadgeGrid.tsx
 * @readme   ../README.md
 * @purpose  Grid display of all badges -- earned badges have reveal animation and trophy SVG, unearned show locked SVG.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useAppTheme } from '@/core/theme';
import { useAcademy } from '../AcademyContext';
import { BADGE_CATALOG } from '../badges';
import { IconTrophy, IconBadgeLocked } from '../icons/AcademyIcons';

export function BadgeGrid() {
  const { theme } = useAppTheme();
  const { badges } = useAcademy();
  const earnedIds = new Set(badges.map((b) => b.badgeId));
  const dur = theme.motion.durations;
  const ease = theme.motion.easing;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(auto-fill, minmax(140px, 1fr))',
        },
        gap: 2,
        '@keyframes badge-flip': {
          '0%': { transform: 'perspective(400px) rotateY(90deg)', opacity: 0 },
          '60%': { transform: 'perspective(400px) rotateY(-10deg)', opacity: 1 },
          '80%': { transform: 'perspective(400px) rotateY(5deg)' },
          '100%': { transform: 'perspective(400px) rotateY(0)' },
        },
      }}
    >
      {BADGE_CATALOG.map((badge, index) => {
        const earned = earnedIds.has(badge.id);
        return (
          <Box
            key={badge.id}
            role="listitem"
            aria-label={`${badge.titleDefault}, ${earned ? 'earned' : 'locked'}`}
            sx={{
              p: 2,
              minHeight: 44,
              borderRadius: `${theme.layout.radius.lg}px`,
              border: `1px solid ${earned ? alpha(theme.rawSemanticColors.statusSuccess, 0.3) : theme.semanticColors.borderSubtle}`,
              backgroundColor: earned
                ? alpha(theme.rawSemanticColors.statusSuccess, 0.04)
                : theme.semanticColors.surfaceCard,
              opacity: earned ? 1 : 0.4,
              textAlign: 'center',
              transition: `all ${dur.normal}ms ${ease.standard}`,
              '@media (prefers-reduced-motion: no-preference)': {
                ...(earned
                  ? {
                      animation: `badge-flip ${dur.slow}ms ${ease.decelerate}`,
                      animationDelay: `${index * 60}ms`,
                      animationFillMode: 'backwards',
                    }
                  : {}),
              },
              '&:hover': earned
                ? {
                    transform: `translateY(${theme.motion.interaction.hoverLift}px) scale(${theme.motion.interaction.cardScale})`,
                    boxShadow: `0 8px 24px -8px ${alpha(theme.rawSemanticColors.statusSuccess, 0.2)}`,
                  }
                : {},
            }}
          >
            <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'center' }}>
              {earned ? <IconTrophy size={28} /> : <IconBadgeLocked size={28} />}
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: theme.semanticColors.textPrimary, mb: 0.25 }}>
              {badge.titleDefault}
            </Typography>
            <Typography sx={{ fontSize: 10, color: theme.semanticColors.textMuted, lineHeight: 1.3 }}>
              {badge.descriptionDefault}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
